import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { preloadLogos } from "./config/logoConfig";
import initGlobalErrorHandler from "./utils/globalErrorHandler";

// Initialize global error handler immediately
initGlobalErrorHandler();

// Pages
import LandingPage from "./pages/LandingPageNew";
import HomePage from "./pages/HomePage";
import AuthPage from "./pages/AuthPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import CompleteProfilePage from "./pages/CompleteProfilePage";
import AuthCallback from "./pages/AuthCallback";
import MicrosoftCallback from "./pages/MicrosoftCallback";
import ForceResetPasswordPage from "./pages/ForceResetPasswordPage";
import ArtistPage from "./pages/ArtistPage";
import ArtistsPage from "./pages/ArtistsPage";
import TracksPage from "./pages/TracksPage";
import TrackPage from "./pages/TrackPage";
import VideosPage from "./pages/VideosPage";
import VideoPage from "./pages/VideoPage";
import ChartsPage from "./pages/ChartsPage";
import CreatorChartPage from "./pages/CreatorChartPage";
import EventsPage from "./pages/EventsPage";
import EventDetailPage from "./pages/EventDetailPage";
import BattlesPage from "./pages/BattlesPage";
import BattleDetailPage from "./pages/BattleDetailPage";
import StudiosPage from "./pages/StudiosPage";
import PricingPage from "./pages/PricingPage";
import DashboardPage from "./pages/DashboardPage";
import SearchPage from "./pages/SearchPage";
import SubscriptionSuccessPage from "./pages/SubscriptionSuccessPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import SettingsPage from "./pages/SettingsPage";
import FeesPage from "./pages/FeesPage";
import FounderPage from "./pages/FounderPage";
import CommunityGuidelinesPage from "./pages/CommunityGuidelinesPage";
import DMCAPage from "./pages/DMCAPage";
import MarketplacePage from "./pages/MarketplacePage";
import BeatMarketplacePage from "./pages/BeatMarketplacePage";
import CollabPlaylistsPage from "./pages/CollabPlaylistsPage";
import StudioModePage from "./pages/StudioModePage";
import ContactPage from "./pages/ContactPage";
import AdminSupportTicketsPage from "./pages/AdminSupportTicketsPage";
import NotFoundPage from "./pages/NotFoundPage";
import ErrorPage from "./pages/ErrorPage";
import UserNotFoundPage from "./pages/UserNotFoundPage";
import CoinSuccessPage from "./pages/CoinSuccessPage";
import LivePage from "./pages/LivePage";
import GoLivePage from "./pages/GoLivePage";
import WatchStreamPage from "./pages/WatchStreamPage";
import LiveRulesPage from "./pages/LiveRulesPage";
import LiveReplayPage from "./pages/LiveReplayPage";
import AnalyticsPage from "./pages/AnalyticsPage";
// New Multi-Role Pages
import InterviewsPage from "./pages/InterviewsPage";
import InterviewDetailPage from "./pages/InterviewDetailPage";
import CreativesPage from "./pages/CreativesPage";
import PodcasterPage from "./pages/PodcasterPage";
import StreamerPage from "./pages/StreamerPage";
import VideographerPage from "./pages/VideographerPage";
import EngineerPage from "./pages/EngineerPage";
import RoleSelectionPage from "./pages/RoleSelectionPage";
import RoleSetupPage from "./pages/RoleSetupPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import BattleLeaderboardPage from "./pages/BattleLeaderboardPage";
import PlaylistsPage from "./pages/PlaylistsPage";
import PlaylistPage from "./pages/PlaylistPage";
import PerksPage from "./pages/PerksPage";
import PerksTermsPage from "./pages/PerksTermsPage";
import MySubscriptionsPage from "./pages/MySubscriptionsPage";
import ListenerPerksPage from "./pages/ListenerPerksPage";
import SocialSyncPage from "./pages/SocialSyncPage";
import BattleRoomPage from "./pages/BattleRoomPage";
import CreatorEditorPage from "./pages/CreatorEditorPage";
import PromoDashboard from "./pages/PromoDashboard";
import AdminSocialPage from "./pages/AdminSocialPage";
import AdminGrantTierPage from "./pages/AdminGrantTierPage";
import AdminFeaturedPage from "./pages/AdminFeaturedPage";
import AdminContentWipePage from "./pages/AdminContentWipePage";
import AdminSpotlightPage from "./pages/AdminSpotlightPage";
import AdminStoriesPage from "./pages/AdminStoriesPage";
import AdminRolesPage from "./pages/AdminRolesPage";
import AdminChartRequestsPage from "./pages/AdminChartRequestsPage";
import AdminAnalyticsDashboardPage from "./pages/AdminAnalyticsDashboardPage";
import CommunitySpotlightPage from "./pages/CommunitySpotlightPage";
import OrganizationProfilePage from "./pages/OrganizationProfilePage";
import ClaimHandlePage from "./pages/ClaimHandlePage";
import BattleBotPage from "./pages/BattleBotPage";
import ViralEnginePage from "./pages/ViralEnginePage";
import ContentLockDashboard from "./pages/ContentLockDashboard";
import CreatorSubscriptionsPage from "./pages/CreatorSubscriptionsPage";
import VoiceBattlePage from "./pages/VoiceBattlePage";

// Public Profile Pages
import PublicProducerPage from "./pages/PublicProducerPage";
import PublicDesignerPage from "./pages/PublicDesignerPage";
import GenreBrowserPage from "./pages/GenreBrowserPage";
import FindProducersPage from "./pages/FindProducersPage";
import FindCreativesPage from "./pages/FindCreativesPage";
import CreativeProfilePage from "./pages/CreativeProfilePage";

// Feed, Stories, Soundstage Clips
import FeedPage from "./pages/FeedPage";
import SpotlightFeedPage from "./pages/SpotlightFeedPage";
import SoundstageSpotlightPage from "./pages/SoundstageSpotlightPage";
import ArticlePage from "./pages/ArticlePage";
import ReelsPage from "./pages/ReelsPage";
import ReelCreatePage from "./pages/ReelCreatePage";
import StoriesViewerPage from "./pages/StoriesViewerPage";
import CreatePostPage from "./pages/CreatePostPage";
import CreateStoryPage from "./pages/CreateStoryPage";
import PostDetailPage from "./pages/PostDetailPage";

// Hire Board
import HireBoardPage from "./pages/HireBoardPage";
import GigDetailPage from "./pages/GigDetailPage";

// Role-specific Signup Pages
import ListenerSignupPage from "./pages/signup/ListenerSignupPage";
import ArtistSignupPage from "./pages/signup/ArtistSignupPage";
import PodcasterSignupPage from "./pages/signup/PodcasterSignupPage";
import StreamerSignupPage from "./pages/signup/StreamerSignupPage";
import StudioSignupPage from "./pages/signup/StudioSignupPage";
import VideographerSignupPage from "./pages/signup/VideographerSignupPage";
import EngineerSignupPage from "./pages/signup/EngineerSignupPage";
import GraphicDesignerSignupPage from "./pages/signup/GraphicDesignerSignupPage";
import ProducerSignupPage from "./pages/signup/ProducerSignupPage";

// Role-specific Dashboards
import ListenerDashboard from "./pages/dashboards/ListenerDashboard";
import ArtistDashboard from "./pages/dashboards/ArtistDashboard";
import ContentCreatorDashboard from "./pages/dashboards/ContentCreatorDashboard";
import PodcasterDashboard from "./pages/dashboards/PodcasterDashboard";
import StreamerDashboard from "./pages/dashboards/StreamerDashboard";
import StudioDashboard from "./pages/dashboards/StudioDashboard";
import VideographerDashboard from "./pages/dashboards/VideographerDashboard";
import EngineerDashboard from "./pages/dashboards/EngineerDashboard";
import GraphicDesignerDashboard from "./pages/dashboards/GraphicDesignerDashboard";
import ProducerDashboard from "./pages/dashboards/ProducerDashboard";

// Auth Pages
// NOTE: TwoFactorVerifyPage removed - 2FA disabled per product decision
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import PhoneVerificationPage from "./pages/PhoneVerificationPage";
// Role-specific Onboarding
import ListenerOnboarding from "./pages/onboarding/ListenerOnboarding";
import ArtistOnboarding from "./pages/onboarding/ArtistOnboarding";

// Public Profile
import PublicProfilePage from "./pages/PublicProfilePage";

// Notifications
import NotificationsPage from "./pages/NotificationsPage";
import NotificationSettingsPage from "./pages/NotificationSettingsPage";

// Beat Store & Music Studio
import BeatStorePage from "./pages/BeatStorePage";
import MusicStudioPage from "./pages/MusicStudioPage";

// Requests Inbox
import RequestsInboxPage from "./pages/RequestsInboxPage";

// Messaging
import MessagesPage from "./pages/MessagesPage";
import ConversationPage from "./pages/ConversationPage";

// Advertiser
import AdvertiserSignupPage from "./pages/AdvertiserSignupPage";
import AdvertiserDashboardPage from "./pages/AdvertiserDashboardPage";
import CampaignCreatePage from "./pages/CampaignCreatePage";
import AdSuccessPage from "./pages/AdSuccessPage";

// Admin Pages
import AdminErrorMonitorPage from "./pages/AdminErrorMonitorPage";
import AdminHealthPage from "./pages/AdminHealthPage";
import AdminDataHygienePage from "./pages/AdminDataHygienePage";
import AdminRouteAuditPage from "./pages/AdminRouteAuditPage";
import AdminPermissionMatrixPage from "./pages/AdminPermissionMatrixPage";
import AdminMonetizationPage from "./pages/AdminMonetizationPage";
import AdminControlCenterPage from "./pages/AdminControlCenterPage";
import HealthAuditPage from "./pages/HealthAuditPage";
import AdminAutomationPage from "./pages/AdminAutomationPage";
import AdminAnnouncementsPage from "./pages/AdminAnnouncementsPage";
import AdminCleanupPage from "./pages/AdminCleanupPage";

// Creator Pages
import CreatorWalletPage from "./pages/CreatorWalletPage";
import CreatorEarningsPage from "./pages/CreatorEarningsPage";
import DonateSuccessPage from "./pages/DonateSuccessPage";
import TipSuccessPage from "./pages/TipSuccessPage";
import OnboardingPage from "./pages/OnboardingPage";
import CreateHubPage from "./pages/CreateHubPage";
import DonateCancelPage from "./pages/DonateCancelPage";
import SupportersPage from "./pages/SupportersPage";
import PromoteContentPage from "./pages/PromoteContentPage";
import PromotionSuccessPage from "./pages/PromotionSuccessPage";
import PromotionsPage from "./pages/PromotionsPage";
import NewReleasesPage from "./pages/NewReleasesPage";
import CoinStorePage from "./pages/CoinStorePage";
import DMVPulsePage from "./pages/DMVPulsePage";
import CreatorStudioPlusPage from "./pages/CreatorStudioPlusPage";
import CreatorDashboardPage from "./pages/CreatorDashboardPage";
import PayoutSettingsPage from "./pages/PayoutSettingsPage";
import StudioPlusSuccessPage from "./pages/StudioPlusSuccessPage";
import AICoachPage from "./pages/AICoachPage";
import CreatorAcademyPage from "./pages/CreatorAcademyPage";
import ReferralsPage from "./pages/ReferralsPage";

// Components
import { Navbar } from "./components/Navbar";
import { MiniPlayer } from "./components/MiniPlayer";
import { EarlyAccessBanner, ReadOnlyBanner } from "./components/PlatformStatus";
import { AnnouncementProvider } from "./components/AnnouncementProvider";
import { PlayerProvider } from "./context/PlayerContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ErrorBoundary from "./components/ErrorBoundary";
import { RequireHandle } from "./components/RequireHandle";
import PhoneVerificationWrapper from "./components/PhoneVerificationWrapper";
import { GuestProvider } from "./context/GuestContext";
import { LocationProvider } from "./context/LocationContext";
import LoginPromptModal from "./components/LoginPromptModal";
import LocationPickerModal from "./components/LocationPickerModal";
import { GamificationProvider } from "./context/GamificationContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";

import { trackPageView, initAnalytics } from "./utils/analytics";
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  
  // Initialize analytics and track page views
  useEffect(() => {
    initAnalytics(user?.user_id);
  }, []);
  
  // Track page views on route change
  useEffect(() => {
    trackPageView(user?.user_id);
  }, [location.pathname, user?.user_id]);
  
  // Preload logo assets on mount
  useEffect(() => {
    preloadLogos();
  }, []);
  
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Logged-in creators get the Creator Dashboard as home; guests get marketing landing
  const Home = user ? CreatorDashboardPage : HomePage;

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/home" element={<Home />} />
      <Route path="/dashboard" element={<CreatorDashboardPage />} />
      <Route path="/landing" element={<HomePage />} />
      <Route path="/auth" element={<LoginPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signin" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/complete-profile" element={<CompleteProfilePage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/claim-handle" element={<ClaimHandlePage />} />
      <Route path="/select-role" element={<RoleSelectionPage />} />
      <Route path="/setup-role" element={<RoleSetupPage />} />
      
      {/* Forgot Password Flow */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/force-reset-password" element={<ForceResetPasswordPage />} />
      <Route path="/auth/microsoft" element={<MicrosoftCallback />} />
      
      {/* Phone Verification */}
      <Route path="/verify-phone" element={<PhoneVerificationPage />} />
      
      {/* Role-specific Signup Routes */}
      <Route path="/signup/listener" element={<ListenerSignupPage />} />
      <Route path="/signup/artist" element={<ArtistSignupPage />} />
      <Route path="/signup/podcaster" element={<PodcasterSignupPage />} />
      <Route path="/signup/streamer" element={<StreamerSignupPage />} />
      <Route path="/signup/studio" element={<StudioSignupPage />} />
      <Route path="/signup/videographer" element={<VideographerSignupPage />} />
      <Route path="/signup/engineer" element={<EngineerSignupPage />} />
      <Route path="/signup/graphic-designer" element={<GraphicDesignerSignupPage />} />
      <Route path="/signup/producer" element={<ProducerSignupPage />} />
      
      {/* Role-specific Onboarding */}
      <Route path="/listener/onboarding" element={<ListenerOnboarding />} />
      <Route path="/artist/onboarding" element={<ArtistOnboarding />} />
      
      {/* Role-specific Dashboards - /dashboard/{role} format */}
      <Route path="/dashboard/artist" element={<ArtistDashboard />} />
      <Route path="/dashboard/content-creator" element={<ContentCreatorDashboard />} />
      <Route path="/dashboard/listener" element={<ListenerDashboard />} />
      <Route path="/dashboard/podcaster" element={<PodcasterDashboard />} />
      <Route path="/dashboard/streamer" element={<StreamerDashboard />} />
      <Route path="/dashboard/studio" element={<StudioDashboard />} />
      <Route path="/dashboard/videographer" element={<VideographerDashboard />} />
      <Route path="/dashboard/engineer" element={<EngineerDashboard />} />
      <Route path="/dashboard/graphic-designer" element={<GraphicDesignerDashboard />} />
      <Route path="/dashboard/producer" element={<ProducerDashboard />} />
      {/* Owner dashboard - owners use artist dashboard */}
      <Route path="/dashboard/owner" element={<ArtistDashboard />} />
      <Route path="/owner/dashboard" element={<ArtistDashboard />} />
      {/* Requests Inbox - for all creators */}
      <Route path="/dashboard/requests" element={<RequestsInboxPage />} />
      <Route path="/requests/inbox" element={<RequestsInboxPage />} />
      
      {/* Direct Messages */}
      <Route path="/messages" element={<MessagesPage />} />
      <Route path="/messages/:conversationId" element={<ConversationPage />} />
      
      {/* Legacy dashboard routes for backwards compatibility */}
      <Route path="/listener/dashboard" element={<ListenerDashboard />} />
      <Route path="/artist/dashboard" element={<ArtistDashboard />} />
      <Route path="/content-creator/dashboard" element={<ContentCreatorDashboard />} />
      <Route path="/podcaster/dashboard" element={<PodcasterDashboard />} />
      <Route path="/streamer/dashboard" element={<StreamerDashboard />} />
      <Route path="/studio/dashboard" element={<StudioDashboard />} />
      <Route path="/videographer/dashboard" element={<VideographerDashboard />} />
      <Route path="/engineer/dashboard" element={<EngineerDashboard />} />
      
      {/* Feed, Stories, Soundstage Clips */}
      <Route path="/feed" element={<SpotlightFeedPage />} />
      <Route path="/reels" element={<ReelsPage />} />
      <Route path="/reels/create" element={<ReelCreatePage />} />
      <Route path="/reels/:reelId" element={<ReelsPage />} />
      <Route path="/stories/:handle" element={<StoriesViewerPage />} />
      <Route path="/stories/create" element={<CreateStoryPage />} />
      <Route path="/post/create" element={<CreatePostPage />} />
      <Route path="/post/:postId" element={<PostDetailPage />} />
      
      {/* Public Profiles - canonical route is /u/:handle */}
      {/* Supports: /u/:handle, /u/id/:userId (fallback), /profile/:handle (legacy) */}
      <Route path="/u/id/:userId" element={<PublicProfilePage />} />
      <Route path="/u/:handle" element={<PublicProfilePage />} />
      <Route path="/profile/:handle" element={<PublicProfilePage />} />
      <Route path="/user-not-found" element={<UserNotFoundPage />} />
      <Route path="/user-not-found/:handle" element={<UserNotFoundPage />} />
      
      {/* Organization Profiles */}
      <Route path="/org/:handle" element={<OrganizationProfilePage />} />
      
      <Route path="/artists" element={<ArtistsPage />} />
      <Route path="/artist/:artistId" element={<ArtistPage />} />
      <Route path="/tracks" element={<TracksPage />} />
      <Route path="/releases" element={<NewReleasesPage />} />
      <Route path="/new-releases" element={<NewReleasesPage />} />
      <Route path="/coins" element={<CoinStorePage />} />
      <Route path="/pulse" element={<DMVPulsePage />} />
      <Route path="/dmv-pulse" element={<DMVPulsePage />} />
      <Route path="/studio-plus" element={<CreatorStudioPlusPage />} />
      <Route path="/creator-studio-plus" element={<CreatorStudioPlusPage />} />
      <Route path="/studio-plus/success" element={<StudioPlusSuccessPage />} />
      <Route path="/settings/payouts" element={<PayoutSettingsPage />} />
      <Route path="/coach" element={<AICoachPage />} />
      <Route path="/ai-coach" element={<AICoachPage />} />
      <Route path="/academy" element={<CreatorAcademyPage />} />
      <Route path="/academy/:slug" element={<CreatorAcademyPage />} />
      <Route path="/referrals" element={<ReferralsPage />} />
      <Route path="/refer" element={<ReferralsPage />} />
      <Route path="/track/:trackId" element={<TrackPage />} />
      <Route path="/videos" element={<VideosPage />} />
      <Route path="/video/:videoId" element={<VideoPage />} />
      <Route path="/charts" element={<ChartsPage />} />
      <Route path="/charts/top-20" element={<ChartsPage />} />
      <Route path="/charts/top-20-videos" element={<ChartsPage />} />
      <Route path="/charts/creator/:handle" element={<CreatorChartPage />} />
      <Route path="/events" element={<EventsPage />} />
      <Route path="/events/:eventId" element={<EventDetailPage />} />
      <Route path="/battles" element={<BattlesPage />} />
      <Route path="/battles/bot" element={<BattleBotPage />} />
      <Route path="/battle-bot" element={<BattleBotPage />} />
      <Route path="/voice-battle" element={<VoiceBattlePage />} />
      <Route path="/battles/:battleId" element={<BattleDetailPage />} />
      <Route path="/studios" element={<StudiosPage />} />
      <Route path="/hire-board" element={<HireBoardPage />} />
      <Route path="/hire-board/:gigId" element={<GigDetailPage />} />
      <Route path="/interviews" element={<InterviewsPage />} />
      <Route path="/interviews/:interviewId" element={<InterviewDetailPage />} />
      <Route path="/creatives" element={<CreativesPage />} />
      <Route path="/podcasters/:podcasterId" element={<PodcasterPage />} />
      <Route path="/streamers/:streamerId" element={<StreamerPage />} />
      <Route path="/videographers/:videographerId" element={<VideographerPage />} />
      <Route path="/engineers/:engineerId" element={<EngineerPage />} />
      <Route path="/producers/:handle" element={<PublicProducerPage />} />
      <Route path="/find-producers" element={<FindProducersPage />} />
      <Route path="/discover" element={<FindCreativesPage />} />
      <Route path="/creative/:handle" element={<CreativeProfilePage />} />
      <Route path="/designers/:handle" element={<PublicDesignerPage />} />
      <Route path="/marketplace" element={<MarketplacePage />} />
      <Route path="/beats" element={<BeatMarketplacePage />} />
      <Route path="/collab-playlists" element={<CollabPlaylistsPage />} />
      <Route path="/studio" element={<StudioModePage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/fees" element={<FeesPage />} />
      <Route path="/why" element={<FounderPage />} />
      <Route path="/about" element={<FounderPage />} />
      <Route path="/legacy-dashboard" element={<DashboardPage />} />
      <Route path="/search" element={<SearchPage />} />
      <Route path="/genres" element={<GenreBrowserPage />} />
      <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
      <Route path="/coins/success" element={<CoinSuccessPage />} />
      <Route path="/promote" element={<PromoteContentPage />} />
      <Route path="/promotions/success" element={<PromotionSuccessPage />} />
      <Route path="/promotions/cancel" element={<PromoteContentPage />} />
      <Route path="/promotions" element={<PromotionsPage />} />
      <Route path="/live" element={<LivePage />} />
      <Route path="/go-live/:streamId" element={<GoLivePage />} />
      <Route path="/watch/:streamId" element={<WatchStreamPage />} />
      <Route path="/live/rules" element={<LiveRulesPage />} />
      <Route path="/live/replay/:replayId" element={<LiveReplayPage />} />
      <Route path="/analytics" element={<AnalyticsPage />} />
      <Route path="/leaderboard" element={<LeaderboardPage />} />
      <Route path="/battle-leaderboard" element={<BattleLeaderboardPage />} />
      <Route path="/playlists" element={<PlaylistsPage />} />
      <Route path="/playlists/:playlistId" element={<PlaylistPage />} />
      <Route path="/perks" element={<PerksPage />} />
      <Route path="/listener-perks" element={<ListenerPerksPage />} />
      <Route path="/perks/terms" element={<PerksTermsPage />} />
      <Route path="/my-subscriptions" element={<MySubscriptionsPage />} />
      <Route path="/social-sync" element={<SocialSyncPage />} />
      <Route path="/sound-lab" element={<Navigate to="/feed" replace />} />
      <Route path="/ai-producer" element={<Navigate to="/feed" replace />} />
      <Route path="/ai-vocalist" element={<Navigate to="/feed" replace />} />
      <Route path="/ai-twin" element={<Navigate to="/feed" replace />} />
      <Route path="/battle-room/:battleId" element={<BattleRoomPage />} />
      <Route path="/battle-room" element={<BattleRoomPage />} />
      <Route path="/creator-studio" element={<Navigate to="/creator-editor" replace />} />
      <Route path="/creator-editor" element={<CreatorEditorPage />} />
      <Route path="/visual-lab" element={<Navigate to="/feed" replace />} />
      <Route path="/viral-engine" element={<ViralEnginePage />} />
      <Route path="/content-locks" element={<ContentLockDashboard />} />
      <Route path="/creator-subscriptions" element={<CreatorSubscriptionsPage />} />
      <Route path="/promo/dashboard" element={<PromoDashboard />} />
      <Route path="/promo/success" element={<PromoDashboard />} />
      <Route path="/promo/cancel" element={<PromoDashboard />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/settings" element={<SettingsPage />} />
      <Route path="/notifications" element={<NotificationsPage />} />
      <Route path="/settings/notifications" element={<NotificationSettingsPage />} />
      <Route path="/beat-store/success" element={<BeatStorePage />} />
      <Route path="/beat-store/:trackId" element={<BeatStorePage />} />
      <Route path="/music-studio" element={<MusicStudioPage />} />
      <Route path="/community-guidelines" element={<CommunityGuidelinesPage />} />
      <Route path="/dmca" element={<DMCAPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/soundstage-spotlight" element={<SoundstageSpotlightPage />} />
      <Route path="/spotlights-news" element={<Navigate to="/soundstage-spotlight" replace />} />
      <Route path="/spotlight" element={<Navigate to="/soundstage-spotlight" replace />} />
      <Route path="/news/:articleId" element={<ArticlePage />} />
      <Route path="/community-spotlight" element={<CommunitySpotlightPage />} />
      <Route path="/community" element={<Navigate to="/community-spotlight" replace />} />
      <Route path="/community/:handle" element={<OrganizationProfilePage />} />
      
      {/* Advertiser Routes */}
      <Route path="/advertise" element={<Navigate to="/ads/create" replace />} />
      <Route path="/signup/advertiser" element={<AdvertiserSignupPage />} />
      <Route path="/advertiser/dashboard" element={<Navigate to="/ads/manager" replace />} />
      <Route path="/advertiser/campaigns/new" element={<Navigate to="/ads/create" replace />} />
      <Route path="/ads/manager" element={<AdvertiserDashboardPage />} />
      <Route path="/ads/create" element={<CampaignCreatePage />} />
      <Route path="/ads/success" element={<AdSuccessPage />} />
      
      {/* Admin Routes */}
      <Route path="/admin/support-tickets" element={<AdminSupportTicketsPage />} />
      <Route path="/admin/social" element={<AdminSocialPage />} />
      <Route path="/admin/grant-tier" element={<AdminGrantTierPage />} />
      <Route path="/admin/featured" element={<AdminFeaturedPage />} />
      <Route path="/admin/content-wipe" element={<AdminContentWipePage />} />
      <Route path="/admin/spotlight" element={<AdminSpotlightPage />} />
      <Route path="/admin/stories" element={<AdminStoriesPage />} />
      <Route path="/admin/roles" element={<AdminRolesPage />} />
      <Route path="/admin/chart-requests" element={<AdminChartRequestsPage />} />
      <Route path="/admin/analytics" element={<AdminAnalyticsDashboardPage />} />
      <Route path="/admin/errors" element={<AdminErrorMonitorPage />} />
      <Route path="/admin/health" element={<AdminHealthPage />} />
      <Route path="/admin/data-hygiene" element={<AdminDataHygienePage />} />
      <Route path="/admin/route-audit" element={<AdminRouteAuditPage />} />
      <Route path="/admin/permissions" element={<AdminPermissionMatrixPage />} />
      <Route path="/admin/monetization" element={<AdminMonetizationPage />} />
      
      {/* Admin Control Center - Single Source of Truth */}
      <Route path="/admin/control-center" element={<AdminControlCenterPage />} />
      <Route path="/admin/health-audit" element={<HealthAuditPage />} />
      <Route path="/admin/automation" element={<AdminAutomationPage />} />
      <Route path="/admin/announcements" element={<AdminAnnouncementsPage />} />
      <Route path="/admin/cleanup" element={<AdminCleanupPage />} />
      
      {/* Admin catch-all: /admin and any unknown /admin/* route → redirect to control center */}
      <Route path="/admin" element={<Navigate to="/admin/control-center" replace />} />
      <Route path="/admin/*" element={<Navigate to="/admin/control-center" replace />} />
      
      {/* Creator Pages */}
      <Route path="/creator/wallet" element={<CreatorWalletPage />} />
      <Route path="/creator/earnings" element={<CreatorEarningsPage />} />
      
      {/* Donate Routes */}
      <Route path="/donate/success" element={<DonateSuccessPage />} />
      <Route path="/tip/success" element={<TipSuccessPage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/create" element={<CreateHubPage />} />
      <Route path="/donate/cancel" element={<DonateCancelPage />} />
      <Route path="/supporters" element={<SupportersPage />} />
      
      {/* 404 - Must be last */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

import AIAssistant from "./components/AIAssistant";
import { CallProvider } from "./context/CallContext";
import IncomingCallModal from "./components/IncomingCallModal";
import CallScreen from "./components/CallScreen";
import { PushNotificationPrompt } from "./components/PushNotificationPrompt";

function ThemedToaster() {
  const { theme } = useTheme();
  return <Toaster position="bottom-right" theme={theme === 'light' ? 'light' : 'dark'} />;
}

function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider>
          <ThemeProvider>
          <LocationProvider>
          <GuestProvider>
          <PlayerProvider>
            <CallProvider>
              <GamificationProvider>
              <div className="min-h-screen bg-background">
                <Navbar />
                <AnnouncementProvider />
                <main className="pb-24">
                  <RequireHandle>
                    <PhoneVerificationWrapper>
                      <AppRouter />
                    </PhoneVerificationWrapper>
                  </RequireHandle>
                </main>
                <MiniPlayer />
                <AIAssistant />
                <ThemedToaster />
                <LoginPromptModal />
                <LocationPickerModal />
                <IncomingCallModal />
                <CallScreen />
                <PushNotificationPrompt />
              </div>
              </GamificationProvider>
            </CallProvider>
          </PlayerProvider>
          </GuestProvider>
          </LocationProvider>
          </ThemeProvider>
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
